import apiClient, { buildQueryParams } from '../utils/apiClient';
import { handleError } from '../utils/errorHandler';
import { API_ENDPOINTS } from '../constants/apiEndpoints.constants';

/**
 * Service for Vehicle Types API calls
 */
const vehicleTypeService = {
  /**
   * Get all vehicle types with pagination and filters
   */
  getAllTypes: async (params = {}) => {
    try {
      const {
        page = 1,
        limit = 50,
        search = '',
        status = '',
        sortBy = 'createdAt',
        sortOrder = 'desc',
      } = params;

      const queryParams = buildQueryParams({
        page,
        limit,
        search,
        status,
        sortBy,
        sortOrder,
      });

      return await apiClient.get(`${API_ENDPOINTS.VEHICLE_TYPES.LIST}?${queryParams}`);
    } catch (error) {
      throw new Error(handleError(error, 'vehicleTypeService.getAllTypes'));
    }
  },

  /**
   * Get a single vehicle type by ID
   */
  getTypeById: async (id) => {
    try {
      return await apiClient.get(API_ENDPOINTS.VEHICLE_TYPES.GET_BY_ID(id));
    } catch (error) {
      throw new Error(handleError(error, 'vehicleTypeService.getTypeById'));
    }
  },

  /**
   * Create a new vehicle type
   */
  createType: async (typeData) => {
    try {
      return await apiClient.post(API_ENDPOINTS.VEHICLE_TYPES.CREATE, typeData);
    } catch (error) {
      throw new Error(handleError(error, 'vehicleTypeService.createType'));
    }
  },

  /**
   * Update a vehicle type
   */
  updateType: async (id, typeData) => {
    try {
      return await apiClient.put(API_ENDPOINTS.VEHICLE_TYPES.UPDATE(id), typeData);
    } catch (error) {
      throw new Error(handleError(error, 'vehicleTypeService.updateType'));
    }
  },

  /**
   * Delete a vehicle type
   */
  deleteType: async (id) => {
    try {
      return await apiClient.delete(API_ENDPOINTS.VEHICLE_TYPES.DELETE(id));
    } catch (error) {
      throw new Error(handleError(error, 'vehicleTypeService.deleteType'));
    }
  },

  /**
   * Delete multiple vehicle types
   */
  deleteMultipleTypes: async (ids) => {
    try {
      return await apiClient.delete(API_ENDPOINTS.VEHICLE_TYPES.DELETE_MULTIPLE, { data: { ids } });
    } catch (error) {
      throw new Error(handleError(error, 'vehicleTypeService.deleteMultipleTypes'));
    }
  },

  /**
   * Toggle vehicle type status
   */
  toggleTypeStatus: async (id) => {
    try {
      return await apiClient.patch(API_ENDPOINTS.VEHICLE_TYPES.TOGGLE_STATUS(id));
    } catch (error) {
      throw new Error(handleError(error, 'vehicleTypeService.toggleTypeStatus'));
    }
  },

  /**
   * Get all vehicle types from public API (for vendors and Flutter)
   * Returns types in format: { status: 200, data: { itemTypes: [...] } }
   * Each item has: { id, name, description, status, image }
   */
  getPublicTypes: async () => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.V1.GET_ALL_CATEGORIES);
      // L'API retourne { status: 200, data: { itemTypes: [...] } }
      // On transforme pour avoir la même structure que getAllTypes
      if (response.data && response.data.itemTypes) {
        // Convertir id en _id pour cohérence avec le reste de l'application
        const types = response.data.itemTypes.map(type => ({
          ...type,
          _id: type.id,
        }));
        return {
          success: true,
          data: types,
        };
      }
      return response;
    } catch (error) {
      throw new Error(handleError(error, 'vehicleTypeService.getPublicTypes'));
    }
  },
};

export default vehicleTypeService;


